export default {
  overview: {
    title: "Settings",
    form: {
      company: {
        title: "Company",
      },
      logo: {
        title: "Logo",
        upload: "Upload logo",
        change: "Change logo",
        remove: "Remove logo",
        hint: "PNG or JPEG, up to 2 MB.",
      },
      currency_title: "Currency Settings",
      currency_hint:
        "Default currency and currency locale decide how amounts are displayed. The locale sets the separators and where the currency symbol goes.",
      default_user_role: "Default Role",
      default_user_role_hint: "Pre-filled for every new login user and can still be changed in the form.",
      default_currency: "Default Currency",
      currency_locale: "Currency Locale",
      first_day_of_week: "First day of week",
      first_day_of_week_hint:
        "Applies to every date picker, to calendars, to My times and to the weekly table of the project costs.",
      week_start: {
        monday: "Monday",
        sunday: "Sunday",
      },
      calculation: {
        title: "Running Post-Calculation",
        employer_contribution: "Employer's contribution (social security)",
        employer_contribution_hint:
          "Surcharge on labour cost for the real full cost. Applied to every employee unless individually overridden. 0 % = no surcharge.",
      },
      // Day submissions from the app: whether the location is required (`daySubmissionLocationRequired`)
      day_submission: {
        title: "Day submissions",
        location_required: "Require the location with every day submission",
        location_required_hint:
          "The app records only once the employee has allowed the location and turned it on. If the phone finds none, it sends anyway and adds the location up to 15 minutes after sending.",
      },
      // Stamping in the app: whether clocking in and adding a crew day need the location (`timeEntryLocationRequired`)
      time_entry: {
        title: "Time tracking",
        location_required: "Require the location for time tracking in the app",
        location_required_hint:
          "The app clocks in and adds a crew day only once the location is allowed and turned on. If the phone finds none, it goes ahead anyway, and clocking out always works.",
      },
      address: {
        title: "Company Address",
        hint: "This address is the sender on every PDF, such as invoices and [[cr.pl]].",
        street: "Street",
        houseNumber: "House number",
        postcode: "Postcode",
        place: "Place",
        country: "Country",
        state: "State",
      },
      document_details: {
        title: "Business details",
        hint: "For the footer of your PDF documents: a letterhead prints a detail wherever one of its blocks names it.",
        phone: "Phone",
        email: "Email",
        website: "Website",
        managingDirector: "Managing director",
        registerCourt: "Register court",
        registerNumber: "Register number",
        vatId: "VAT ID",
        taxNumber: "Tax number",
        bankName: "Bank",
        iban: "IBAN",
        bic: "BIC",
      },
      save: "Save",
    },
  },
  general: {
    title: "General",
    max_storage_size: "Maximum File-Storage size",
  },
  // Settings → Terminology: what the company calls its reports (services/terminology.js).
  terminology: {
    title: "Terminology",
    section: "Records",
    hint: "The name appears throughout Lewara, in notifications and e-mails and on newly printed PDFs. Reports printed before keep theirs. Each language has names of its own; PDFs and e-mails use the German ones.",
    language: "Language of the names",
    columns: {
      record: "Record",
      name: "Name",
      plural: "Plural",
      article: "Article",
    },
    records: {
      HOURLY_WAGE_REPORT: "Work billed by effort, confirmed by the customer",
      CONSTRUCTION_REPORT: "Daily record of the construction site",
      HOURLY_WAGE_INVOICE: "Invoice for the work billed by effort",
    },
    articles: {
      MASCULINE: "der",
      FEMININE: "die",
      NEUTER: "das",
    },
    default: "Default",
    // In the language menu, next to every language in which the company renamed records; otherwise "Default".
    custom_count: "{n} custom",
    too_long: "At most {max} characters",
    // Per language of the names, not of the interface: German names are one word, because the endings
    // (des Regiescheins, den Regiescheinen) are added at the end (services/terminology.js).
    invalid: {
      de: "One word, compound or hyphenated",
      en: "Only letters, digits, spaces and . & / ' -",
    },
    invalid_save: "A name is not valid. Please correct the marked field.",
    preview_label: "How it reads:",
    preview: "[[Hwr:A/An]] [[hwr]] is attached to Monday's [[cr]]. Three [[hwr.pl]] make [[hwi:a/an]] [[hwi]].",
  },
  absence: {
    singular: "Type of Absence",
    title: "Types of Absence",
    default_types: "Default Types of Absence",
    custom_types: "Custom Types of Absence",
    default_types_hint:
      "Every workspace comes with these. Their colours appear in personnel planning and on days off in time tracking.",
    custom_types_hint: "Your own reasons for absence, with a name and a colour. The plus saves right away.",
    no_custom_types: "No custom absence types available",
    form: {
      name: "New Absence Type",
      paid: "Paid",
    },
    name: "Name",
    color: "Colour",
    paid: "Paid",
    paid_hint:
      "Time tracking credits paid absences to the time account, up to the day's target. A change applies to past days as well.",
  },
  rights: {
    title: "Permissions",
    roles_hint:
      "A role bundles permissions and applies to everyone who holds it. Which role someone has is set on the employee. Administrator always holds every permission.",
    // Shown without EDIT_ROLES in place of "Create a new Role": the roles open and can be read, not
    // changed. Only the Administrator role holds the permission.
    edit_denied: "Only the Administrator role can create, change and delete roles.",
    table: {
      id: "Role ID",
      name: "Roles",
    },
    detail: {
      title: "Role",
      subtitle: "Id",
      category: {
        permission: "Permission",
        show: "Show",
        edit: "Edit",
        delete: "Delete",
      },
      input: "Role Name",
      error: "Please enter the name here",
      no_selected: "No Role selected",
    },
    actions: {
      create: "Create a new Role",
      save: "Save",
      cancel: "Cancel",
      delete: {
        title: "Delete Role",
        text: "Are you sure to delete this role?",
        agree: "Agree",
        disagree: "Disagree",
      },
    },
  },
  pdf: {
    title: "PDF templates",
    back: "Back to the PDF templates",
    name: "Name",
    edit: "Edit",
    copy: "Copy",
    delete: "Delete",
    save: "Save",
    save_copy: "Save as a copy",
    undo: "Undo",
    redo: "Redo",
    built_in: "Built in",
    letterhead: "Letterhead",
    letterhead_hint: "Logo, sender, footer, font and colours. Several templates can use the same letterhead.",
    open_letterhead: "Open letterhead",
    sample_document: "Sample document",
    new_text: "New text",
    color_none: "None",
    color_clear: "Remove {name}",
    compact_notice: "Letterheads are designed on a larger screen. Here you see how this one prints.",
    letterhead_editor: { title: "Letterhead" },
    template_editor: { title: "PDF template" },
    mode: { design: "Design", pdf: "PDF" },
    system_banner: {
      letterhead:
        "This is the built-in letterhead. What you change is saved as a letterhead of your own; the built-in one stays as it is.",
      template:
        "This is the built-in template. What you change is saved as a template of your own; the built-in one stays as it is.",
      template_default:
        "This is the built-in template. What you change is saved as a template of your own and prints from then on; the built-in one stays as it is.",
    },
    letterheads: {
      title: "Letterheads",
      hint: "A letterhead decides how every page looks: logo, sender, title, footer, font and colours.",
      add: "New letterhead",
      used_by: "Used by no template | Used by one template | Used by {n} templates",
      system_meta: "What the built-in templates print on",
      documents: "For no document | For one document | For {n} documents",
      delete_in_use: "Delete: used by one template | Delete: used by {n} templates",
      delete_title: "Delete letterhead?",
      delete_text: "“{name}” and its pictures are deleted. Printed documents stay as they are.",
    },
    documents: {
      title: "Documents",
      hint: "Each row shows what the document prints with. A project picks another template in its configuration. Changes apply to future prints; printed reports stay as they are.",
      customize: "Customize",
      add: "Add another template",
      others: "One more template | {n} more templates",
    },
    use: {
      action: "Use for documents",
      title: "Use “{name}”",
      subtitle: "The documents picked print on this letterhead from now on.",
      current: "Now: {name}",
      already: "Already prints on it",
      submit: "Use",
    },
    templates: {
      default_chip: "Default",
      make_default: "Use as default",
      on_letterhead: "Letterhead: {name}",
      delete_title: "Delete template?",
      delete_text: "“{name}” is deleted. Printed documents stay as they are.",
      delete_projects:
        "One project prints with it and takes the default afterwards. | {n} projects print with it and take the default afterwards.",
      delete_default:
        "It is the default: afterwards every project without a choice of its own prints with “Lewara Standard”.",
    },
    preview: {
      title: "Preview",
      action: "Preview",
      empty: "The preview appears in a moment.",
      failed: "The preview could not be made.",
      overflow: "Too small for its text: {names} | Too small for their text: {names}",
    },
    problem: {
      name_missing: "Name missing",
      name_too_long: "Name longer than {max} characters",
      blocks: "More than {max} blocks",
      theme_color: "Invalid colour (design)",
      frame: "Content area: margin outside the page",
      number: "{name}: size missing",
      off_page: "{name}: outside the page",
      size: "{name}: larger than the page",
      font_size: "{name}: font size 4 to 72 pt",
      stroke: "{name}: line width 0 to 20 pt",
      text_too_long: "{name}: text longer than {max} characters",
      color: "{name}: invalid colour",
      headline_too_long: "Headline longer than {max} characters",
      label_too_long: "{name}: label longer than {max} characters",
      labels: "{name}: more than {max} labels",
      field_too_long: "Field name longer than {max} characters",
    },
    sections: {
      title: "Content",
      hint: "Show, hide, reorder and adjust sections. What makes a document what it is always stays.",
      move: "Move {name} (arrow keys)",
      required: "Always printed",
      hidden_fields: "one field hidden | {n} fields hidden",
      headline: "Headline",
      align: "Alignment",
      fields: "Fields",
      field_name: "Name of {name}",
    },
    section: {
      HEADER_DATA: "Header",
      SHIFTS: "Shifts",
      SERVICE_ENTRIES: "Services",
      CONSTRUCTION_SERVICES: "Free services",
      COMMENTS: "Remarks",
      SPECIAL_OCCURRENCES: "Special occurrences",
      ATTACHMENTS: "Attachments",
      DESCRIPTION: "Description of work",
      USERS: "Staff",
      LABOR: "Labour",
      MACHINES: "Machines",
      MATERIALS: "Materials",
      ADDITIONAL_EFFORTS: "Additional services",
      GRAND_TOTAL: "Total",
      POSITIONS: "Positions",
      SUMMARY: "Summary",
      DAYS: "Days",
      TIME_ACCOUNT: "Working-time account",
      FOOTNOTES: "Notes",
    },
    field: {
      DATE: "Date",
      EXECUTION_DATE: "Date of execution",
      PROJECT_NUMBER: "Order number",
      CUSTOMER: "Client",
      PROJECT_NAME: "Construction project",
      CREATED_BY: "Created by",
      CATEGORIES: "Categories",
      PROJECT: "Project",
      PROJECT_ADDRESS: "Project address",
      TIMES: "Duration, break and working time",
      WEATHER_READINGS: "Weather at three times",
      WEATHER_COURSE: "Weather course",
      CREW: "Staff",
      MACHINES: "Machines",
      NOTE: "Note",
      LABOR: "Labour",
      MATERIALS: "Materials",
      SUBTOTAL: "Subtotal",
      IMAGES: "Pictures",
      PDFS: "PDF documents",
      DESCRIPTION: "Description of work",
      EMPLOYEE: "Employee",
      PERSONNEL_NUMBER: "Personnel number",
      MONTH: "Month",
      WORK_TIME_MODEL: "Working-time model",
      WEEKDAY: "Day",
      FROM_TIME: "Start",
      TO_TIME: "End",
      BREAK: "Break",
      WORKED: "Working time",
      TARGET: "Target",
      BALANCE: "Balance",
      STATE: "Remark",
      PERIOD: "Period",
      CUTOFF: "As of",
      POSITION_COUNT: "Positions",
      COMPLETION: "Completion",
      CONTRACT_VALUE: "Contract value",
      EARNED_VALUE: "Total performance value",
      PERIOD_EARNED_VALUE: "Performance value in the period",
      OPEN_VALUE: "Open remainder",
      PLANNED_QUANTITY: "Contract quantity",
      UNIT: "Unit",
      ACTUAL_QUANTITY: "Actual quantity",
      PERIOD_QUANTITY: "Quantity in the period",
      PROGRESS: "Progress",
      RUNNING_NUMBER: "Running number",
      POSITION: "Position",
      SERVICE: "Service",
      QUANTITY: "Quantity",
      TEXT: "Text",
      REFERENCE: "Reference",
      NAME: "Name",
      TOTAL_QUANTITY: "Total quantity",
      UNIT_PRICE: "Unit price",
      BILLED_TOTAL: "Billed amount",
      TOTAL: "Total",
      MACHINE: "Machine",
      MATERIAL: "Material",
      EFFORT: "Additional service",
      COUNT: "Count",
      HOURS: "Hours",
      USAGE: "Usage",
      BILLED: "Billed",
      DURATION: "Duration",
    },
    align: {
      DOCUMENT: "As in the document",
      LEFT: "Left",
      CENTER: "Centred",
      RIGHT: "Right",
      JUSTIFIED: "Justified",
    },
    valign: { TOP: "Top", MIDDLE: "Middle", BOTTOM: "Bottom" },
    signature: {
      title: "Signature",
      none_by_default: "Only from a signature block that names this document under “Documents”.",
      on_letterhead: "The labels are on the letterhead's signature block.",
      visible: "Print the signature line",
    },
    frame: {
      title: "Content area",
      hint: "How far the document's own content stays from the page edge: the dashed area on the page, whose edges you drag. Everything outside belongs to the letterhead.",
      template_hint:
        "How far the document's own content stays from the page edge. Everything outside belongs to the letterhead.",
      from_template: "Content area here from the template “{name}”",
      exact: "Enter exactly",
      like_letterhead: "As on the letterhead",
      own: "Own for this template",
      change: "Change",
      first: "First page",
      following: "Following pages",
      top: "Top",
      bottom: "Bottom",
      left: "Left",
      right: "Right",
    },
    canvas: {
      label: "Letterhead page. Arrow keys move the selected block, Delete removes it.",
    },
    pages: { ALL: "All pages", FIRST: "First page", FOLLOWING: "Following pages" },
    palette: {
      add: "Add",
      texts: "Texts",
      shapes: "Pictures and shapes",
      blocks: "On the page",
      forward: "Bring forward",
      backward: "Send backward",
    },
    kind: {
      TEXT: "Text",
      LOGO: "Logo",
      IMAGE: "Picture",
      LINE: "Line",
      RECT: "Area",
      DOCUMENT_TITLE: "Title and number",
      PAGE_NUMBER: "Page number",
      SIGNATURE: "Signature",
    },
    inspector: {
      duplicate: "Duplicate",
      delete: "Remove",
      x: "Left",
      y: "Top",
      width: "Width",
      height: "Height",
      text: "Text",
      page_format: "Format",
      insert_field: "Insert field",
      font_size: "Font size",
      bold: "Bold",
      italic: "Italic",
      text_color: "Text colour",
      title_text: "Title",
      number_text: "Number",
      signature_label: "Label {n}",
      signature_add: "Label",
      signature_remove: "Remove label",
      signature_reset: "As in each document",
      signature_note:
        "Applies to every document on this letterhead. Documents without a signature of their own (cost statements, [[hwi]]) print it only where they are chosen under “Documents”.",
      logo_workspace: "Company logo",
      logo_lewara: "Lewara",
      logo_missing: "No company logo yet: until one is uploaded (Settings → General), the Lewara logo stands here.",
      upload_image: "Upload picture",
      delete_image: "Delete picture",
      fill: "Fill",
      stroke: "Line",
      stroke_width: "Width",
      where: "Where",
      pages: "Pages",
      documents: "Documents",
      all_documents: "All documents",
      background: "Behind the content",
    },
    theme: {
      title: "Design",
      font: "Font",
      font_hint:
        "Helvetica is the font used so far and only knows Western European characters. The Liberation fonts print every character.",
      text_color: "Text colour",
      headline_style: "Section headline",
      accent: "Headline background",
      primary: "Line under the headline",
      highlight: "Invoice position bar",
      highlight_dark: "Invoice total bar",
    },
    font: {
      HELVETICA: "Helvetica (so far)",
      LIBERATION_SANS: "Liberation Sans (like Arial)",
      LIBERATION_SERIF: "Liberation Serif (like Times)",
      LIBERATION_MONO: "Liberation Mono (like Courier)",
    },
    headline_style: {
      TINTED_BAR: "Coloured bar with line",
      RULE: "Line only",
      PLAIN: "Text only",
    },
    placeholder_group: { company: "Company", document: "Document", page: "Page" },
    placeholder: {
      company_name: "Company name",
      company_street: "Street",
      company_houseNumber: "House number",
      company_postcode: "Postcode",
      company_place: "Town",
      company_phone: "Phone",
      company_email: "Email",
      company_website: "Website",
      company_managingDirector: "Managing director",
      company_registerCourt: "Register court",
      company_registerNumber: "Register number",
      company_vatId: "VAT ID",
      company_taxNumber: "Tax number",
      company_bankName: "Bank",
      company_iban: "IBAN",
      company_bic: "BIC",
      document_title: "Document title",
      document_number: "Document number",
      document_date: "Document date",
      print_date: "Print date",
      project_number: "Project number",
      project_name: "Project name",
      customer_name: "Client",
      employee_name: "Employee",
      page_number: "Page number",
      page_total: "Total pages",
    },
    document: {
      CONSTRUCTION_REPORT: "[[CR]]",
      CONSTRUCTION_REPORT_COST: "Cost and revenue statement ([[cr]])",
      HOURLY_WAGE_REPORT: "[[HWR]]",
      HOURLY_WAGE_REPORT_COST: "Cost and revenue statement ([[hwr]])",
      HOURLY_WAGE_INVOICE: "[[HWI]]",
      MONTHLY_TIMESHEET: "Monthly timesheet",
      SERVICE_CATALOG_PROGRESS: "Progress statement",
    },
  },
}
